import React, { useState } from 'react';
import { taskAPI } from '../../../services/api';
import { useAuth } from '../../../AuthContext';

const getStatusText = (status) => {
    const statuses = ['Pending', 'In Progress', 'Completed', 'Cancelled'];
    return statuses[status] || 'Unknown';
};

const getPriorityText = (priority) => {
    const priorities = ['Low', 'Medium', 'High', 'Urgent'];
    return priorities[priority] || 'Unknown';
};

const getStatusClass = (status) => {
    const classes = ['status-pending', 'status-progress', 'status-completed', 'status-cancelled'];
    return classes[status] || 'status-pending';
};

const getPriorityClass = (priority) => {
    const classes = ['priority-low', 'priority-medium', 'priority-high', 'priority-urgent'];
    return classes[priority] || 'priority-medium';
};

const formatCompletedAt = (completedAt) => {
    if (!completedAt) return '';
    const d = new Date(completedAt);
    return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
    });
};

const TasksTab = ({ tasks, onRefresh, onAddTask, onEditTask }) => {
    const [showCompleted, setShowCompleted] = useState(false);
    const { user } = useAuth();

    const activeTasks = tasks.filter(t => t.status !== 2 && t.status !== 3);
    const completedTasks = tasks.filter(t => t.status === 2);
    const cancelledTasks = tasks.filter(t => t.status === 3);
    const finishedTasks = tasks.filter(t => t.status === 2 || t.status === 3);

    const canEdit = (task) => user?.role?.toLowerCase() === 'admin' || task.assignedToId === user?.id;
    const canDelete = canEdit;

    const updateStatus = async (taskId, newStatus) => {
        try {
            const task = tasks.find(t => t.id === taskId);
            await taskAPI.update(taskId, {
                title: task.title,
                description: task.description,
                priority: task.priority,
                status: newStatus,
                assignedToId: task.assignedToId,
                customerId: task.customerId,
                projectId: task.projectId,
                dueDate: task.dueDate,
                completedAt: newStatus === 2 ? new Date().toISOString() : null
            });
            onRefresh();
        } catch (err) {
            console.error('Error updating task status:', err);
        }
    };

    const handleDelete = async (taskId) => {
        if (!window.confirm('Delete this task?')) return;
        try {
            await taskAPI.delete(taskId);
            onRefresh();
        } catch (err) {
            console.error('Error deleting task:', err);
        }
    };

    return (
        <div className="tasks-tab">
            <div className="tab-header">
                <h3>Tasks ({activeTasks.length} active)</h3>
                <div className="tab-header-actions">
                    {finishedTasks.length > 0 && (
                        <button
                            className={`btn-filter ${showCompleted ? 'active' : ''}`}
                            onClick={() => setShowCompleted(!showCompleted)}
                        >
                            <i className={`fas ${showCompleted ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            {' '}{showCompleted ? 'Hide' : 'Show'} Completed ({finishedTasks.length})
                        </button>
                    )}
                    <button className="btn-create-task" onClick={onAddTask}>
                        + Add Task
                    </button>
                </div>
            </div>

            {/* Active Tasks */}
            {activeTasks.length === 0 && !showCompleted ? (
                <div className="tab-empty">
                    <i className="fas fa-tasks"></i>
                    <p>No active tasks for this project</p>
                </div>
            ) : activeTasks.length > 0 && (
                <div className="tasks-table-container">
                    <table className="tasks-table">
                        <thead>
                            <tr>
                                <th>Task</th>
                                <th>Priority</th>
                                <th>Assigned To</th>
                                <th>Due Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeTasks.map(task => (
                                <tr key={task.id} className={`task-row ${getStatusClass(task.status)}`}>
                                    <td className="task-info">
                                        <div className="task-title">{task.title}</div>
                                        {task.description && (
                                            <div className="task-description">{task.description}</div>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
                                            {getPriorityText(task.priority)}
                                        </span>
                                    </td>
                                    <td className="assigned-to">
                                        {task.assignedTo?.username || 'Unassigned'}
                                    </td>
                                    <td className="due-date">
                                        {task.dueDate
                                            ? new Date(task.dueDate).toLocaleDateString()
                                            : 'No due date'}
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusClass(task.status)}`}>
                                            {getStatusText(task.status)}
                                        </span>
                                    </td>
                                    <td className="task-actions">
                                        <div className="action-buttons">
                                            <select
                                                className="status-select"
                                                value={task.status}
                                                onChange={(e) => updateStatus(task.id, parseInt(e.target.value))}
                                                disabled={!canEdit(task)}
                                            >
                                                <option value="0">Pending</option>
                                                <option value="1">In Progress</option>
                                                <option value="2">Completed</option>
                                                <option value="3">Cancelled</option>
                                            </select>
                                            {canEdit(task) && (
                                                <button
                                                    className="action-btn btn-edit"
                                                    onClick={() => onEditTask(task)}
                                                    title="Edit task"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                            )}
                                            {canDelete(task) && (
                                                <button
                                                    className="action-btn btn-delete"
                                                    onClick={() => handleDelete(task.id)}
                                                    title="Delete task"
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Completed / Cancelled Tasks */}
            {showCompleted && finishedTasks.length > 0 && (
                <div className="completed-tasks-section">
                    <h4 className="completed-tasks-heading">Completed & Cancelled Tasks</h4>
                    <div className="completed-tasks-list">
                        {finishedTasks.map(task => (
                            <div key={task.id} className={`completed-task-row ${task.status === 3 ? 'cancelled' : ''}`}>
                                <div className="completed-task-info">
                                    <div className="completed-task-title">
                                        <span className={`completed-status-icon ${task.status === 2 ? 'done' : 'cancel'}`}>
                                            <i className={`fas ${task.status === 2 ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                        </span>
                                        {task.title}
                                    </div>
                                    <div className="completed-task-meta">
                                        <span>{task.status === 2 ? 'Completed' : 'Cancelled'}</span>
                                        {task.completedAt && <span>{formatCompletedAt(task.completedAt)}</span>}
                                        {task.assignedTo && <span>by {task.assignedTo.username}</span>}
                                    </div>
                                </div>
                                <div className="completed-task-actions">
                                    {canEdit(task) && task.status === 2 && (
                                        <button
                                            className="btn-reopen"
                                            onClick={() => updateStatus(task.id, 0)}
                                            title="Reopen task"
                                        >
                                            Reopen
                                        </button>
                                    )}
                                    {canDelete(task) && (
                                        <button
                                            className="action-btn btn-delete"
                                            onClick={() => handleDelete(task.id)}
                                            title="Delete task"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TasksTab;
